import React, {useEffect, useState} from 'react'
import {Toggle} from '@thryvlabs/maverick'
import {RadioCircle} from '../../../../accountManagement/shared/RadioCircle'
import {Button} from '../../../../accountManagement/shared/Button'
import {
  CloseIcon,
  PlanArrowIcon,
} from '../../../../accountManagement/shared/icons'
import {currencyFormat} from '../../../../utils/currency-format'
import {getPriceForCountry} from '../../utils/getPriceForCountry'
import {PlanDynamicField} from './PlanDynamicField'

import {SeoAddonPanel} from './panels/SeoAddonPanel'
import {LeadsLiftPanel} from './panels/LeadsLiftPanel'
import {KeapAddonPanel} from './panels/KeapAddonPanel'
import {WebsiteAddonPanel} from './panels/WebsiteAddonPanel'
import {HvacAddonPanel} from './panels/HvacAddonPanel'
import {TLAddonPanel} from './panels/TLAddonPanel'
import {GrowthPackagePanel} from './panels/GrowthPackagePanel'
import {KcpCertificationPanel} from './panels/KcpCertificationPanel'
import {WebsiteSettingsModal} from '../WebsiteSettingsModal'
import {WEBSITE_DESIGN_FEE_PLAN} from '../../utils/websiteDesignFee'
import {
  MC_2_0_SIGNATUREPLANS,
  MC_2_0_SIGPREMIERPLANS,
} from '../../../../utils/constants'
import {
  getFieldsFromPlan,
  getDefaultValues,
  validatePlanFields,
} from './planFieldsConfig'
import {
  GridSlot,
  PanelWrapper,
  PanelLeft,
  PanelHeader,
  PanelTitle,
  CloseButton,
  PlanOption,
  PlanText,
  PlanPeriod,
  PlanArrow,
  PanelRight,
  ProductOptions,
  FieldsGrid,
  AddToOrderRow,
  NoSelectionMessage,
  EntitlementsList,
  EntitlementsTitle,
  EntitlementItem,
} from './ProductPlanSelector.styles'
import {useSelector} from 'react-redux'
import {selectEffectiveRole} from '../../../../app-store/selector'
import {useUserFeatures} from '../../../../context/user-features-context'

const WEBSITE_ADDON_CODE = 'addon-5-website_difm'
const WEBSITE_ENTITLEMENT_CODE = 'ent-5-website'
const SOCIAL_CONTENT_GEN_ENTITLEMENT_CODE = 'ent-5-socialcontentgen'
const CERT_PARTNER_SLP_CODE = 'addon-5-keap-certpart-slp-m2m'
const BC_UNLIMITED_CODE = 'bc_unlm-5-thryv-m2m'

// A plan added without its website entitlement is saved under its alternate code, so match
// the alternate too, unless that alternate is visible as its own card.
const getEquivalentPlanCodes = (plan, visiblePlans) => {
  if (!plan?.code) return []
  if (!plan.alternatePlanCodes?.length) return [plan.code]
  const otherVisibleCodes = new Set(
    (visiblePlans ?? []).filter(p => p.code !== plan.code).map(p => p.code),
  )
  return [
    plan.code,
    ...plan.alternatePlanCodes
      .map(e => e.alternate_plan_code)
      .filter(
        code => code && code !== plan.code && !otherVisibleCodes.has(code),
      ),
  ]
}

// Map plan codes to their custom panel component.
// Plans listed here bypass the generic fields+entitlements UI.
const PLAN_PANELS = {
  'seobase-5-thryv-m2m': SeoAddonPanel,
  'addon-5-leadslift-m2m': LeadsLiftPanel,
  'keap_max-5-thryv-m2m': KeapAddonPanel,
  'keap_max-5-thryv-annual': KeapAddonPanel,
  'keap_max-5-thryv-part-m2m': KeapAddonPanel,
  'keap_max-5-thryv-part-annual': KeapAddonPanel,
  'keap_pro-5-thryv-m2m': KeapAddonPanel,
  'keap_pro-5-thryv-annual': KeapAddonPanel,
  'keap_ultimate-5-thryv-m2m': KeapAddonPanel,
  'keap_ultimate-5-thryv-annual': KeapAddonPanel,
  'keap_ultimate-5-thryv-part-m2m': KeapAddonPanel,
  'keap_ultimate-5-thryv-part-annual': KeapAddonPanel,
  'keap_max-enterprise-fran-5-thryv-m2m': KeapAddonPanel,
  'keap_max-franchise-5-thryv-m2m': KeapAddonPanel,
  'keap_max-franchisee-5-thryv-m2m': KeapAddonPanel,
  'keap_max-frandev-5-thryv-m2m': KeapAddonPanel,
  [WEBSITE_ADDON_CODE]: WebsiteAddonPanel,
  'maxhvac-5-thryv-m2m': HvacAddonPanel,
  'maxroof-5-thryv-m2m': HvacAddonPanel,
  'maxelect-5-thryv-m2m': HvacAddonPanel,
  'maxplumb-5-thryv-m2m': HvacAddonPanel,
  'tl-mc-5-thryv-m2m': TLAddonPanel,
  'tl-tax-5-thryv-m2m': TLAddonPanel,
  'addon-5-mcstartgr': GrowthPackagePanel,
  'addon-5-mcexpangr': GrowthPackagePanel,
  'keap_kcp-cert-5-fee-annual': KcpCertificationPanel,
}

export function ProductPlanSelector({
  group,
  allPlans = [],
  selectedCountry,
  selectedProducts,
  disabledPlanCodes,
  validExtensionList,
  isAnnual,
  isBillingLocked,
  showMCGroupAnnualToggle,
  isSocialContentGenGrandfathered,
  productConfigInfo,
  showGrowthPackageOnboarding,
  onBillingTypeChange,
  onClose,
  onAddProduct,
  onUpdateProduct,
}) {
  const hasBothBillingTypes =
    group.plans?.some(p => p.productBillingType === 'Annual') &&
    group.plans?.some(p => p.productBillingType !== 'Annual')
  const userFeatures = useUserFeatures()
  const effectiveRole = useSelector(selectEffectiveRole)
  const canSeeCertPartEntitlement =
    effectiveRole.includes('Admin') || effectiveRole.includes('PartnerOps')
  const isMCGroup = group?.name?.toLowerCase().includes('marketing center')
  const isEnabledForAUNZTgpUpgrade = Boolean(
    userFeatures?.Enable_AU_NZ_TGP_Upgrade,
  )
  const isEnabledForUSTgpUpgrade = Boolean(userFeatures?.Enable_US_TGP_Upgrade)

  const isTgpUpgradeEnabled =
    (['AU', 'NZ'].includes(selectedCountry) && isEnabledForAUNZTgpUpgrade) ||
    (selectedCountry === 'US' && isEnabledForUSTgpUpgrade)
  const showAnnualToggle =
    hasBothBillingTypes && (!isMCGroup || showMCGroupAnnualToggle)

  const visiblePlans = hasBothBillingTypes
    ? group.plans?.filter(p =>
        isAnnual
          ? p.productBillingType === 'Annual'
          : p.productBillingType !== 'Annual',
      )
    : group.plans

  // Reopening a product group after a plan has been added must land on that
  // plan's saved configuration. Previously multi-plan groups opened with no
  // selection while every added plan was disabled, leaving only the empty
  // "Select a plan" message.
  const getInitialSelectedPlan = () => {
    const addedPlan = visiblePlans?.find(plan =>
      selectedProducts?.some(product =>
        getEquivalentPlanCodes(plan, visiblePlans).includes(product.planCode),
      ),
    )
    return (
      addedPlan?.code ??
      (visiblePlans?.length === 1 ? visiblePlans[0].code : null)
    )
  }
  const [selectedPlan, setSelectedPlan] = useState(getInitialSelectedPlan)
  const [fieldValues, setFieldValues] = useState({})
  const [errors, setErrors] = useState({})
  const [touched, setTouched] = useState({})
  const [selectedEntitlements, setSelectedEntitlements] = useState([])
  // TGP can show both sides of the Signature/Signature Plus pair as direct
  // choices. Keep that direct-card choice intact until the rep explicitly
  // changes the website entitlement, which is itself a plan-selection input.
  const [hasWebsiteEntitlementChanged, setHasWebsiteEntitlementChanged] =
    useState(false)
  const [validatedQuantity, setValidatedQuantity] = useState(null)
  const [isWebsiteModalOpen, setIsWebsiteModalOpen] = useState(false)
  const [websiteTransfer, setWebsiteTransfer] = useState({
    hasExistingWebsite: false,
    domain: null,
    isDomainValid: false,
  })

  useEffect(() => {
    const plan = group.plans?.find(p => p.code === selectedPlan) ?? null
    const fields = getFieldsFromPlan(plan)
    const itemInOrder = selectedProducts?.find(product =>
      getEquivalentPlanCodes(plan, visiblePlans).includes(product.planCode),
    )
    setFieldValues({
      ...getDefaultValues(fields),
      ...(itemInOrder?.configuredOptions ?? {}),
    })
    setErrors({})
    setTouched({})
    setHasWebsiteEntitlementChanged(false)
    setValidatedQuantity(null)
    const catalogEntitlementCodes = new Set(
      (plan?.entitlements ?? []).map(e => e.code),
    )
    const savedEntitlementCodes = new Set(
      (itemInOrder?.addons ?? []).map(addon => addon.code ?? addon.productCode),
    )
    // Addons from Recurly that the catalog doesn't expose as configurable
    // entitlements (e.g. ent-5-ai-webbuilder-sig on the Signature plan) —
    // the UI can't show or toggle them, but they must be carried through
    // unchanged so they aren't silently dropped when the rep modifies other
    // entitlements and clicks "Add to Order".
    const unknownSavedAddons = (itemInOrder?.addons ?? []).filter(addon => {
      const code = addon.code ?? addon.productCode
      return code && !catalogEntitlementCodes.has(code)
    })
    setSelectedEntitlements([
      ...(plan?.entitlements ?? [])
        .filter(
          e =>
            e.isRequired ||
            savedEntitlementCodes.has(e.code) ||
            // Default Website to checked only for a brand-new plan
            (!itemInOrder && e.code === WEBSITE_ENTITLEMENT_CODE),
        )
        .map(e => {
          const savedEntitlement = itemInOrder?.addons?.find(
            addon => addon.code === e.code || addon.productCode === e.code,
          )
          return {
            ...e,
            quantity: savedEntitlement?.quantity ?? e.quantity ?? 1,
          }
        }),
      ...unknownSavedAddons,
    ])
    // selectedProducts is reconstructed by the cart hook on each render;
    // selecting a plan is the deliberate point at which to hydrate the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlan])

  const handleEntitlementChange = (ent, checked) => {
    setSelectedEntitlements(prev =>
      checked
        ? [...prev, {...ent, quantity: ent.quantity ?? 1}]
        : prev.filter(e => e.code !== ent.code),
    )
    if (ent.code === WEBSITE_ENTITLEMENT_CODE) {
      setHasWebsiteEntitlementChanged(true)
      // Reset on every check so re-checking after an
      // uncheck re-prompts rather than reusing a stale answer.
      setWebsiteTransfer({
        hasExistingWebsite: false,
        domain: null,
        isDomainValid: false,
      })
      setIsWebsiteModalOpen(checked)
    }
  }

  const handleBillingToggle = newValue => {
    onBillingTypeChange(group.id, newValue)
    setSelectedPlan(null)
  }

  const handleFieldChange = (key, value) => {
    setFieldValues(prev => ({...prev, [key]: value}))
    setErrors(prev => ({...prev, [key]: null}))
  }

  const handleFieldBlur = key => {
    setTouched(prev => ({...prev, [key]: true}))
    const fields = getFieldsFromPlan(
      group.plans?.find(p => p.code === selectedPlan) ?? null,
    )
    const fieldErrors = validatePlanFields(fields, fieldValues, {
      validExtensionList,
    })
    setErrors(prev => ({...prev, [key]: fieldErrors[key] ?? null}))
  }

  const handleFieldValidated = (field, isValid, res) => {
    if (isValid && field.responseQuantityField) {
      setValidatedQuantity(res?.[field.responseQuantityField] ?? null)
    } else if (!isValid) {
      setValidatedQuantity(null)
    }
  }

  const handleAddToOrder = () => {
    const fields = getFieldsFromPlan(
      group.plans?.find(p => p.code === selectedPlan) ?? null,
    )
    const fieldErrors = validatePlanFields(fields, fieldValues, {
      validExtensionList,
    })
    const allTouched = fields.reduce((acc, f) => ({...acc, [f.key]: true}), {})
    setTouched(allTouched)
    setErrors(fieldErrors)
    if (Object.keys(fieldErrors).length > 0) return

    const plan = group.plans.find(p => p.code === selectedPlan)
    if (!plan) return

    const isOneTimeFee = group.prod_type === 'OneTimeFees'
    const hasWebsiteEntitlement = selectedEntitlements.some(
      e => e.code === WEBSITE_ENTITLEMENT_CODE,
    )

    // Resolve alternate plan code based on website entitlement selection
    const resolvedPlanCode = (() => {
      if (!plan.alternatePlanCodes?.length) return plan.code
      // GEP-7133 / FULSAS-13599: this swap assumes only ONE side of a
      // base/alternate pair (e.g. Signature vs. Signature Plus) is ever
      // rendered as its own selectable card — the normal case, enforced by
      // useCatalogCart's alternatePlanCodesToHide. The TGP upsell override
      // (see productSelectionRules.js) can leave BOTH sides visible at once
      // for the TGP upgrade flow, so a rep can click either card directly.
      // Without this guard, clicking e.g. "Signature Plus" directly could
      // still get silently swapped to "Signature" (or vice versa) based on
      // the entitlement-checked state below — the rep already picked
      // exactly the plan they want, so skip the swap whenever the alternate
      // side is independently present as its own card in this group.
      // TO REVERT: delete this if-block; the swap below is unchanged
      // otherwise, and still applies normally to every other pair in the
      // app where only one side is ever visible.
      if (
        isTgpUpgradeEnabled &&
        !hasWebsiteEntitlementChanged &&
        MC_2_0_SIGNATUREPLANS.includes(plan.code)
      ) {
        const alternateCodes = plan.alternatePlanCodes.map(
          e => e.alternate_plan_code,
        )
        const alternateAlsoVisible = group.plans.some(p =>
          alternateCodes.includes(p.code),
        )
        if (alternateAlsoVisible) return plan.code
      }

      const swapWhenSelected = hasWebsiteEntitlement ? 1 : 0
      const entry = plan.alternatePlanCodes.find(
        e =>
          e.entitlement_code === WEBSITE_ENTITLEMENT_CODE &&
          e.swap_when_selected === swapWhenSelected,
      )
      return entry ? entry.alternate_plan_code : plan.code
    })()
    const resolvedPlan =
      resolvedPlanCode !== plan.code
        ? group.plans.find(p => p.code === resolvedPlanCode) ??
          allPlans.find(p => p.code === resolvedPlanCode) ??
          plan
        : plan

    const product = {
      productId: resolvedPlanCode,
      name: resolvedPlan.name,
      price: getPriceForCountry(resolvedPlan.currencies, selectedCountry),
      productType: isOneTimeFee
        ? 'ONE_TIME_FEE'
        : group.prod_type === 'Center'
        ? 'CENTER'
        : 'APP',
      billingFrequency: isOneTimeFee
        ? null
        : plan.productBillingType === 'Annual'
        ? 'annual'
        : 'monthly',
      quantity: validatedQuantity ?? 1,
      configuredOptions: fieldValues,
      addons: selectedEntitlements.map(ent => {
        const base =
          ent.code === WEBSITE_ENTITLEMENT_CODE
            ? {...ent, configuration: websiteTransfer}
            : ent
        if (base.unitPrice == null && base.currencies) {
          return {
            ...base,
            unitPrice: getPriceForCountry(base.currencies, selectedCountry),
          }
        }
        return base
      }),
      excludes: plan.excludes ?? [],
      autoAddCenters: plan.autoAddCenters ?? [],
      autoAddOneTimeFees:
        hasWebsiteEntitlement &&
        !websiteTransfer.isDomainValid &&
        !MC_2_0_SIGPREMIERPLANS.includes(plan.code)
          ? [WEBSITE_DESIGN_FEE_PLAN]
          : [],
      disableApps: plan.disableApps ?? [],
      disableCenters: plan.disableCenters ?? [],
      maxQty: plan.maxQty ?? null,
      productRank: plan.productRank ?? null,
      downgrades: plan.downgrades ?? [],
      parentProduct: resolvedPlan.parentProduct,
    }

    if (itemInOrder && onUpdateProduct) {
      // The cart item may be saved under the plan's alternate code, which
      // isn't a visible card, so target it explicitly to avoid a duplicate.
      onUpdateProduct(product, {
        previousProductCode: itemInOrder.planCode,
        groupPlanCodes: [
          ...new Set([...group.plans.map(p => p.code), itemInOrder.planCode]),
        ],
        groupId: group.id,
      })
    } else {
      onAddProduct(
        product,
        group.plans.map(p => p.code),
        group.id,
      )
    }
    onClose()
  }

  const selectedPlanObject =
    group.plans?.find(p => p.code === selectedPlan) ?? null
  const itemInOrder = selectedProducts?.find(p =>
    getEquivalentPlanCodes(selectedPlanObject, visiblePlans).includes(
      p.planCode,
    ),
  )
  const planFields = getFieldsFromPlan(selectedPlanObject)
  // Social Content Generation is only ever offered on BC Unlimited for new
  // purchases — BC Professional accounts keep it only if grandfathered in
  // from before the cutoff.
  const entitlements = (selectedPlanObject?.entitlements ?? []).filter(
    ent =>
      (ent.code !== SOCIAL_CONTENT_GEN_ENTITLEMENT_CODE ||
        selectedPlan === BC_UNLIMITED_CODE ||
        isSocialContentGenGrandfathered) &&
      (ent.code !== CERT_PARTNER_SLP_CODE || canSeeCertPartEntitlement),
  )
  // const hasAddons = getAddonsForPlan(selectedPlan).length > 0
  // const isLeadsLift = isLeadsLiftPlan(selectedPlan)
  const CustomPanel = selectedPlan ? PLAN_PANELS[selectedPlan] ?? null : null
  const isWebsiteAddonInCart = selectedProducts?.some(
    p => p.planCode === WEBSITE_ADDON_CODE && !p.alreadyIncluded,
  )

  return (
    <GridSlot>
      <WebsiteSettingsModal
        isOpen={isWebsiteModalOpen}
        onContinue={result => {
          setWebsiteTransfer(result)
          setIsWebsiteModalOpen(false)
        }}
      />
      <PanelWrapper>
        <PanelLeft>
          <PanelHeader>
            <PanelTitle> </PanelTitle>
          </PanelHeader>
          {showAnnualToggle && (
            <div
              style={{marginBottom: '12px', paddingLeft: '4px'}}
              onClick={e => e.stopPropagation()}
            >
              <Toggle
                label="Annual"
                toggleSize="normal"
                onChange={handleBillingToggle}
                toggled={isAnnual}
                disabled={isBillingLocked}
                customSelectedColor="#16A085"
                customUnselectedColor="#808080"
              />
              {isBillingLocked && (
                <p
                  style={{
                    fontSize: '11px',
                    color: '#808080',
                    margin: '4px 0 0',
                  }}
                >
                  Locked to match existing subscription
                </p>
              )}
            </div>
          )}
          {visiblePlans?.map(plan => {
            const isPlanAdded = selectedProducts?.some(p =>
              getEquivalentPlanCodes(plan, visiblePlans).includes(p.planCode),
            )
            const isPlanDisabled = disabledPlanCodes?.has(plan.code)
            return (
              <PlanOption
                key={plan.code}
                type="button"
                isActive={selectedPlan === plan.code}
                isAdded={isPlanAdded}
                isDisabled={isPlanDisabled}
                // Added plans remain selectable so their configuration can be
                // viewed and edited. Add/update handling below prevents a
                // duplicate line item from being created.
                disabled={isPlanDisabled}
                onClick={() => setSelectedPlan(plan.code)}
              >
                <RadioCircle
                  active={selectedPlan === plan.code || isPlanAdded}
                  variant="dot"
                />
                <PlanText style={{flex: 1}} isDisabled={isPlanDisabled}>
                  {plan.name}{' '}
                  {currencyFormat(
                    getPriceForCountry(plan.currencies, selectedCountry),
                    selectedCountry,
                  )}
                  <PlanPeriod>/mo</PlanPeriod>
                </PlanText>
                <PlanArrow isActive={selectedPlan === plan.code}>
                  <PlanArrowIcon />
                </PlanArrow>
              </PlanOption>
            )
          })}
        </PanelLeft>
        <PanelRight>
          <CloseButton type="button" onClick={onClose}>
            <CloseIcon />
          </CloseButton>
          <ProductOptions>
            {selectedPlan === null ? (
              <NoSelectionMessage>
                Select a plan to see its configuration options
              </NoSelectionMessage>
            ) : CustomPanel ? (
              <CustomPanel
                plan={selectedPlanObject}
                selectedCountry={selectedCountry}
                prodType={group.prod_type}
                selectedProducts={selectedProducts}
                itemInOrder={itemInOrder}
                groupPlanCodes={group.plans.map(p => p.code)}
                groupId={group.id}
                validExtensionList={validExtensionList}
                productConfigInfo={productConfigInfo}
                showGrowthPackageOnboarding={showGrowthPackageOnboarding}
                onAddProduct={onAddProduct}
                onUpdateProduct={onUpdateProduct}
                onClose={onClose}
              />
            ) : (
              <>
                {planFields.length > 0 && (
                  <FieldsGrid>
                    {planFields.map(field => (
                      <PlanDynamicField
                        key={field.key}
                        field={field}
                        value={fieldValues[field.key] ?? ''}
                        values={fieldValues}
                        error={touched[field.key] ? errors[field.key] : null}
                        touched={touched[field.key]}
                        onChange={handleFieldChange}
                        onBlur={handleFieldBlur}
                        onValidated={(isValid, res) =>
                          handleFieldValidated(field, isValid, res)
                        }
                        variant="form"
                        size="md"
                      />
                    ))}
                  </FieldsGrid>
                )}
                {entitlements.length > 0 && (
                  <>
                    <EntitlementsTitle>Entitlements</EntitlementsTitle>
                    <EntitlementsList>
                      {entitlements.map(ent => {
                        // ent-5-website gets disabled here once the
                        // standalone addon-5-website_difm is already in the
                        // cart — and shown checked too, since that standalone
                        // product already covers this feature.
                        const isWebsiteEnt =
                          ent.code === WEBSITE_ENTITLEMENT_CODE
                        const isEntDisabled =
                          ent.isRequired || disabledPlanCodes?.has(ent.code)
                        const isEntChecked =
                          ent.isRequired ||
                          selectedEntitlements.some(e => e.code === ent.code) ||
                          (isWebsiteEnt && isWebsiteAddonInCart)
                        // Only the user's own selection triggers the fee
                        // preview — not the "already covered by the
                        // standalone add-on" checked state.
                        const isWebsiteEntSelected =
                          isWebsiteEnt &&
                          selectedEntitlements.some(e => e.code === ent.code)
                        return (
                          <React.Fragment key={ent.code}>
                            <EntitlementItem>
                              <label
                                style={{
                                  cursor: isEntDisabled ? 'default' : 'pointer',
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isEntChecked}
                                  disabled={isEntDisabled}
                                  onChange={e =>
                                    handleEntitlementChange(
                                      ent,
                                      e.target.checked,
                                    )
                                  }
                                />{' '}
                                {ent.name}
                              </label>
                            </EntitlementItem>
                            {isWebsiteEntSelected &&
                              !websiteTransfer.isDomainValid &&
                              !MC_2_0_SIGPREMIERPLANS.includes(
                                selectedPlan,
                              ) && (
                                <EntitlementItem>
                                  <label style={{cursor: 'default'}}>
                                    <input type="checkbox" checked disabled />{' '}
                                    {WEBSITE_DESIGN_FEE_PLAN.name}
                                  </label>
                                </EntitlementItem>
                              )}
                          </React.Fragment>
                        )
                      })}
                    </EntitlementsList>
                  </>
                )}
                <AddToOrderRow>
                  <Button
                    type="button"
                    variant="action-orange"
                    size="sm"
                    onClick={handleAddToOrder}
                  >
                    {itemInOrder ? 'Update Order' : 'Add to Order'}
                  </Button>
                </AddToOrderRow>
              </>
            )}
          </ProductOptions>
        </PanelRight>
      </PanelWrapper>
    </GridSlot>
  )
}
